import { useTranslation } from 'react-i18next';
import { useScrollReveal } from '@/hooks/useScrollReveal';

export default function Hero() {
  const { t } = useTranslation('homepage');
  const { ref, isVisible } = useScrollReveal<HTMLDivElement>(0.1);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=A%20stunning%20Quebec%20residential%20house%20with%20a%20brand%20new%20asphalt%20shingle%20roof%2C%20dramatic%20golden%20hour%20lighting%20with%20warm%20copper%20tones%2C%20light%20snow%20on%20the%20ground%2C%20blue%20sky%20with%20soft%20clouds%2C%20architectural%20photography%20style%2C%20wide%20angle%20landscape%20shot%2C%20professional%20real%20estate%20photography%2C%20crisp%20sharp%20details%2C%20warm%20color%20grading%20with%20rich%20shadows%20and%20highlights%2C%20simple%20clean%20background%2C%20no%20text%20visible&width=1920&height=1080&seq=hero1&orientation=landscape"
          alt="Maison québécoise avec toiture neuve à Lévis par couvreur certifié RBQ"
          className="w-full h-full object-cover"
          loading="eager"
          fetchpriority="high"
          width="1920"
          height="1080"
        />
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-primary/70 via-slate-primary/50 to-slate-primary/80" />
      </div>

      {/* Content */}
      <div
        ref={ref}
        className={`relative z-10 w-full px-4 md:px-6 lg:px-10 pt-20 pb-16 max-w-6xl mx-auto text-center transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        {/* Social proof avatars */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="flex -space-x-2">
            <div className="w-10 h-10 rounded-full bg-copper flex items-center justify-center text-white text-xs font-bold border-2 border-white">ML</div>
            <div className="w-10 h-10 rounded-full bg-slate-light flex items-center justify-center text-white text-xs font-bold border-2 border-white">PD</div>
            <div className="w-10 h-10 rounded-full bg-copper-light flex items-center justify-center text-white text-xs font-bold border-2 border-white">LR</div>
          </div>
          <span className="text-white/90 text-sm font-medium">200+ toitures à Lévis</span>
        </div>

        {/* H1 */}
        <h1 className="font-manrope font-extrabold text-white leading-tight mb-4">
          <span className="block text-[40px] sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl">{t('hero_h1')}</span>
          {t('hero_h1_sub') && (
            <span className="block text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-light mt-2">{t('hero_h1_sub')}</span>
          )}
        </h1>

        {/* Subtitle */}
        <p className="text-white/85 text-base md:text-lg lg:text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
          {t('hero_subtitle')}
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
          <button
            onClick={() => scrollToSection('quote-form')}
            className="group bg-copper hover:bg-copper-dark text-white font-semibold px-8 py-4 rounded-md text-base md:text-lg transition-all duration-300 flex items-center gap-3 whitespace-nowrap"
          >
            {t('cta_get_quote')}
            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
          </button>
          <a
            href="tel:+14184769950"
            aria-label="Appeler Couvreur-Lévis.com au 418-476-9950"
            title="Cliquez pour appeler"
            className="flex items-center gap-3 border-2 border-white/60 hover:border-white text-white font-medium px-6 py-4 rounded-md text-base transition-all duration-300 whitespace-nowrap"
          >
            <i className="ri-phone-line" />
            {t('cta_urgency')}
          </a>
        </div>

        {/* Trust badges */}
        <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6">
          {t('badge_rbq') && (
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
              <i className="ri-shield-check-line text-copper" />
              <span className="text-white text-sm font-medium">{t('badge_rbq')}</span>
            </div>
          )}
          <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
            <div className="flex text-yellow-400 text-xs">
              <i className="ri-star-fill" />
              <i className="ri-star-fill" />
              <i className="ri-star-fill" />
              <i className="ri-star-fill" />
              <i className="ri-star-fill" />
            </div>
            <span className="text-white text-sm font-medium">{t('badge_google')}</span>
          </div>
          <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
            <i className="ri-file-shield-line text-copper" />
            <span className="text-white text-sm font-medium">{t('badge_guarantee')}</span>
          </div>
          <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
            <i className="ri-shield-check-line text-copper" />
            <span className="text-white text-sm font-medium">{t('badge_insurance')}</span>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 animate-bounce">
        <button
          onClick={() => scrollToSection('empathy')}
          aria-label="Défiler vers le contenu"
          className="text-white/60 hover:text-white transition-colors"
        >
          <i className="ri-arrow-down-double-line text-2xl" aria-hidden="true" />
        </button>
      </div>
    </section>
  );
}